import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, History, LogOut, Pencil, Plus, Repeat } from "lucide-react";
import { useState } from "react";
import {useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { useGoBack } from "../../shared/hooks/goBack";
import { api } from "../../shared/api/client";
import {
  Button,
  DataTable,
  ErrorState,
  Field,
  InlineAlert,
  Input,
  LoadingState,
  FilterBar,
  PageHeader,
  StatusBadge,
  type Column,
} from "../../shared/ui";
import type { TeamDetail, TeamMemberView } from "../../shared/types/api";
import { AddMemberDrawer, CloseMembershipDrawer, MoveMemberDrawer } from "./MembershipDrawers";
import { TeamEditDrawer } from "./TeamEditDrawer";

const today = () => new Date().toISOString().slice(0, 10);

/**
 * A team, and who was in it on a given date.
 *
 * <p>The membership table is the clearest expression of the temporal model in the product. Members
 * are added from a date and removed by closing a period — there is no delete, because removing the
 * row would erase the fact that the person was ever there, and with it every report that depends
 * on knowing so.
 */
export function TeamDetailPage() {
  const { teamId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const { goBack, hasHistory } = useGoBack("/teams");
  const { can } = useAuth();

  const asOf = params.get("asOf") ?? today();
  const isHistorical = asOf !== today();
  const manage = can("TEAM_MANAGE");

  const [adding, setAdding] = useState(false);
  const [moving, setMoving] = useState<TeamMemberView | null>(null);
  const [closing, setClosing] = useState<TeamMemberView | null>(null);
  const [editing, setEditing] = useState(false);

  const team = useQuery({
    queryKey: ["team", teamId],
    queryFn: () => api.get<TeamDetail>(`/api/v1/teams/${teamId}`),
  });

  const members = useQuery({
    queryKey: ["team-members", teamId, asOf],
    queryFn: () => api.get<TeamMemberView[]>(`/api/v1/teams/${teamId}/members?asOf=${asOf}`),
  });

  const reload = () => {
    void members.refetch();
    void team.refetch();
  };

  if (team.isPending) {
    return <LoadingState label="Loading team…" />;
  }
  if (team.isError) {
    return <ErrorState error={team.error} onRetry={() => void team.refetch()} />;
  }

  const columns: Column<TeamMemberView>[] = [
    {
      key: "fullName",
      header: "Employee",
      render: (member) => <span className="font-medium text-ink-900">{member.fullName}</span>,
    },
    {
      key: "employeeCode",
      header: "Code",
      render: (member) => (
        <span className="font-mono text-xs text-ink-500">{member.employeeCode}</span>
      ),
    },
    {
      key: "role",
      header: "Role",
      render: (member) => <StatusBadge tone="blue">{member.role.replace(/_/g, " ")}</StatusBadge>,
    },
    { key: "effectiveFrom", header: "From", className: "tnum" },
    {
      key: "effectiveTo",
      header: "To",
      className: "tnum",
      render: (member) =>
        member.effectiveTo ?? <span className="text-ink-400">open-ended</span>,
    },
    {
      key: "membershipPercentage",
      header: "Allocation",
      className: "text-right tnum",
      render: (member) => `${member.membershipPercentage}%`,
    },
  ];

  if (manage && !isHistorical) {
    columns.push({
      key: "actions",
      header: "",
      className: "text-right",
      render: (member) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            title="Move to another team"
            onClick={() => setMoving(member)}
          >
            <Repeat className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title="End this membership"
            className="text-amber-600 hover:bg-amber-50 hover:text-amber-700"
            onClick={() => setClosing(member)}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      ),
    });
  }

  return (
    <>
      <PageHeader
        title={team.data.name}
        subtitle={team.data.description ?? undefined}
      />

      {/* One strip: the way back and what the team is. The name is in the top bar. */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-ink-500">
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeft className="h-4 w-4" />}
          className="-ml-2"
          onClick={goBack}
        >
          {hasHistory ? "Back" : "All teams"}
        </Button>
        <span className="rounded-md bg-ink-100 px-2 py-0.5 font-mono text-xs text-ink-600">
          {team.data.code}
        </span>
        <StatusBadge tone={team.data.status === "ACTIVE" ? "green" : "gray"}>
          {team.data.status}
        </StatusBadge>
        <span>Lead: {team.data.teamLeadName ?? "not assigned"}</span>
      </div>

      <FilterBar actions={
          manage &&
          !isHistorical && (
            <>
              <Button
                variant="secondary"
                icon={<Pencil className="h-4 w-4" />}
                onClick={() => setEditing(true)}
              >
                Edit team
              </Button>
              <Button icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
                Add member
              </Button>
            </>
          )
        }>
          <div className="w-52">
            <Field label="Members as of">
              <Input
                type="date"
                value={asOf}
                onChange={(event) => setParams({ asOf: event.target.value })}
              />
            </Field>
          </div>

          {isHistorical && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              <History className="h-4 w-4 shrink-0" />
              Historical view
            </div>
          )}

          <div className="ml-auto">
            <Button variant="ghost" size="sm" onClick={() => setParams({ asOf: today() })}>
              Back to today
            </Button>
          </div>
      </FilterBar>

      {isHistorical && (
        // Editing a past view would be confusing: the change applies from a date the reader is not
        // currently looking at. The actions are hidden and the reason is stated.
        <div className="mb-4">
          <InlineAlert tone="info">
            This is how the team looked on {asOf}. Switch back to today to change membership.
          </InlineAlert>
        </div>
      )}

      {members.isError ? (
        <ErrorState error={members.error} onRetry={() => void members.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={members.data ?? []}
          rowKey={(member) => member.membershipId}
          loading={members.isPending}
          emptyTitle={isHistorical ? `Nobody was in this team on ${asOf}` : "No members yet"}
          emptyMessage={
            isHistorical
              ? "Membership is recorded with dates, so this is the answer for that day — not a gap in the data."
              : "Add someone from the date their involvement starts."
          }
          emptyAction={
            manage &&
            !isHistorical && (
              <Button icon={<Plus className="h-4 w-4" />} onClick={() => setAdding(true)}>
                Add the first member
              </Button>
            )
          }
        />
      )}

      <AddMemberDrawer
        teamId={teamId}
        teamName={team.data.name}
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={reload}
      />
      <MoveMemberDrawer
        fromTeamId={teamId}
        member={moving}
        onClose={() => setMoving(null)}
        onSaved={reload}
      />
      <CloseMembershipDrawer
        teamId={teamId}
        member={closing}
        onClose={() => setClosing(null)}
        onSaved={reload}
      />
      <TeamEditDrawer
        team={editing ? team.data : null}
        onClose={() => setEditing(false)}
        onSaved={reload}
      />
    </>
  );
}
